import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, RequireAdmin } from '@/lib/auth';
import HomePage from '@/pages/HomePage';
import PaymentPage from '@/pages/PaymentPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import ContactPage from '@/pages/ContactPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import { PrivacyPage, TermsPage, DisclaimerPage } from '@/pages/PolicyPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AuthCallback from '@/pages/AuthCallback';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminConsultationsPage from '@/pages/admin/AdminConsultationsPage';
import AdminPaymentsPage from '@/pages/admin/AdminPaymentsPage';
import AdminPaymentLinksPage from '@/pages/admin/AdminPaymentLinksPage';
import AdminLeadsPage from '@/pages/admin/AdminLeadsPage';
import AdminPostsPage from '@/pages/admin/AdminPostsPage';

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (!hash) window.scrollTo({ top: 0, behavior: 'instant' });
    }, [pathname, hash]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/payments" element={<PaymentPage />} />
            <Route path="/pay" element={<Navigate to="/payments" replace />} />
            <Route path="/book" element={<Navigate to="/contact" replace />} />
            <Route path="/insights" element={<Navigate to="/" replace />} />
            <Route path="/insights/:slug" element={<Navigate to="/" replace />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<RequireAdmin><AdminOverview /></RequireAdmin>} />
            <Route path="/admin/consultations" element={<RequireAdmin><AdminConsultationsPage /></RequireAdmin>} />
            <Route path="/admin/payments" element={<RequireAdmin><AdminPaymentsPage /></RequireAdmin>} />
            <Route path="/admin/payment-links" element={<RequireAdmin><AdminPaymentLinksPage /></RequireAdmin>} />
            <Route path="/admin/leads" element={<RequireAdmin><AdminLeadsPage /></RequireAdmin>} />
            <Route path="/admin/posts" element={<RequireAdmin><AdminPostsPage /></RequireAdmin>} />
            <Route path="*" element={<HomePage />} />
        </Routes>
    );
}

// Stable toast options — extracted to a module constant to avoid re-creating the
// object on every App render (which would needlessly re-render the Toaster).
const TOAST_OPTIONS = {
    style: {
        background: 'rgba(11,15,23,0.95)',
        color: '#F2F4F8',
        border: '1px solid rgba(255,255,255,0.10)',
        backdropFilter: 'blur(12px)',
    },
};

function App() {
    return (
        <div className="App dark">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <AppRouter />
                    <Toaster position="bottom-right" toastOptions={TOAST_OPTIONS} />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
